// Switch the NP-template Vitals and Review-of-Systems sections between their
// structured Quill block-embed form (the boxes + cards from DocumentBody.js'
// `vitals-grid` / `ros-grid` blots) and a plain-text form that can be selected
// and copied like normal prose.
//
// Everything here is a PURE transform over a Quill delta (`{ ops: [...] }`) so
// it can be unit-tested without mounting Quill. MedStudentClinicPage applies
// the returned ops through the same YText / setContents path it already uses
// for "Copy from older".
//
// Field metadata is intentionally duplicated (not imported from DocumentBody.js)
// so this module stays free of the Quill/CSS imports that file carries. Keep the
// keys in sync with VITAL_CELLS / ROS_CARDS there.

const VITAL_FIELDS = [
  { key: 'weight', label: 'Weight', unit: 'lb' },
  { key: 'bmi',    label: 'BMI',    unit: '' },
  { key: 'height', label: 'Height', unit: '' },
  { key: 'bp',     label: 'BP',     unit: 'mmHg' },
  { key: 'hr',     label: 'HR',     unit: 'bpm' },
  { key: 'spo2',   label: 'SpO₂',   unit: '' },
];

const ROS_FIELDS = [
  { key: 'gen',    label: 'GEN',    positive: false },
  { key: 'heent',  label: 'HEENT',  positive: false },
  { key: 'card',   label: 'CARD',   positive: true },
  { key: 'resp',   label: 'RESP',   positive: true },
  { key: 'gi',     label: 'GI',     positive: false },
  { key: 'msk',    label: 'MSK',    positive: false },
  { key: 'skin',   label: 'SKIN',   positive: false },
  { key: 'neuro',  label: 'NEURO',  positive: false },
  { key: 'psych',  label: 'PSYCH',  positive: false },
  { key: 'endo',   label: 'ENDO',   positive: false },
  { key: 'heme',   label: 'HEME',   positive: false },
  { key: 'allerg', label: 'ALLERG', positive: false },
];

// "▌ VITALS" → "vitals"; "▌ REVIEW OF SYSTEMS" → "review of systems".
function normalizeHeading(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}
function isVitalsHeading(text) {
  return normalizeHeading(text) === 'vitals';
}
function isRosHeading(text) {
  const n = normalizeHeading(text);
  return n === 'review of systems' || n === 'ros';
}
// Any "▌ …" bar paragraph (or a recognized section name) marks a section
// boundary — used to reset which section a body line belongs to. The leading
// "▌" glyph is kept here only for backward compatibility with notes saved
// before the markers were removed from the template; current templates instead
// style headings as a bold + colored line (see lineIsStyledHeading).
function isHeadingLine(text) {
  const raw = String(text || '').trim();
  if (raw.startsWith('▌')) return true;
  return isVitalsHeading(text) || isRosHeading(text);
}

// A section heading in the current (marker-free) template is a standalone line
// whose every text run is bold AND colored — e.g. "PHYSICAL EXAM". Bold field
// labels ("Weight:") carry no color, and the colored "NEGATIVE"/italic prose
// runs aren't fully bold, so neither is mistaken for a heading. This is what
// terminates the Vitals/ROS sections now that the "▌" glyph is gone.
function lineIsStyledHeading(line) {
  if (!line || line.kind !== 'text') return false;
  const textOps = (line.ops || []).filter((o) => typeof o.insert === 'string' && o.insert !== '');
  if (textOps.length === 0) return false;
  return textOps.every((o) => o.attributes && o.attributes.bold && o.attributes.color);
}

// "SpO₂" → "spo2", "HEME/LYMPH" → "hemelymph", "Weight" → "weight".
function normalizeLabel(s) {
  return String(s || '').toLowerCase().replace(/₂/g, '2').replace(/[^a-z0-9]+/g, '');
}
function matchFieldKey(label, fields) {
  const n = normalizeLabel(label);
  if (!n) return null;
  for (const f of fields) {
    const fn = normalizeLabel(f.label);
    if (n === fn) return f.key;
  }
  // Tolerate renamed labels that extend a canonical one (HEME → "HEME/LYMPH").
  for (const f of fields) {
    const fn = normalizeLabel(f.label);
    if (fn.length >= 2 && (n.startsWith(fn) || fn.startsWith(n))) return f.key;
  }
  return null;
}

// Split "GEN: Fever, fatigue." into { label: 'GEN', rest: 'Fever, fatigue.' }.
function splitLabeledLine(text) {
  const m = String(text || '').match(/^\s*([^:]{1,40}):\s*([\s\S]*)$/);
  if (!m) return null;
  return { label: m[1].trim(), rest: m[2] };
}

// Break a delta's ops into "lines". Each line is either plain text (with its
// inline ops preserved so untouched lines round-trip byte-for-byte) or a
// standalone block embed (`vitals-grid` / `ros-grid`).
function deltaToLines(ops) {
  const lines = [];
  let buffer = [];
  const flushText = (newlineAttributes) => {
    lines.push({ kind: 'text', ops: buffer, newlineAttributes: newlineAttributes || null });
    buffer = [];
  };
  for (const op of ops || []) {
    if (!op || op.insert == null) continue;
    if (typeof op.insert === 'string') {
      const parts = op.insert.split('\n');
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] !== '') {
          buffer.push(op.attributes ? { insert: parts[i], attributes: op.attributes } : { insert: parts[i] });
        }
        if (i < parts.length - 1) flushText(op.attributes);
      }
    } else if (typeof op.insert === 'object') {
      const key = Object.keys(op.insert)[0];
      if (key === 'vitals-grid' || key === 'ros-grid') {
        if (buffer.length) lines.push({ kind: 'text', ops: buffer, newlineAttributes: null, noNewline: true });
        buffer = [];
        lines.push({ kind: 'embed', embedKey: key, embedValue: op.insert[key], op });
      } else {
        buffer.push(op); // inline embed (e.g. image) — stays inline
      }
    }
  }
  if (buffer.length) lines.push({ kind: 'text', ops: buffer, newlineAttributes: null, noNewline: true });
  return lines;
}

function lineText(line) {
  if (line.kind !== 'text') return '';
  return line.ops.map((o) => (typeof o.insert === 'string' ? o.insert : '')).join('');
}

// Reassemble the line model back into a flat ops array. A text line emits its
// inline ops followed by a newline (carrying any block attributes); an embed
// line emits its original op unchanged.
function linesToOps(lines) {
  const ops = [];
  for (const line of lines) {
    if (line.kind === 'embed') {
      ops.push(line.op);
      continue;
    }
    for (const o of line.ops) ops.push(o);
    if (!line.noNewline) {
      ops.push(line.newlineAttributes ? { insert: '\n', attributes: line.newlineAttributes } : { insert: '\n' });
    }
  }
  return ops;
}

// One "Label: value unit" text line, with a bold label.
function labeledTextLine(label, content) {
  return {
    kind: 'text',
    ops: [
      { insert: `${label}:`, attributes: { bold: true } },
      { insert: ` ${content}` },
    ],
    newlineAttributes: null,
  };
}

// Stored vital cell may be an object `{ value, label }` or a bare string.
function vitalCellValue(cell) {
  if (cell && typeof cell === 'object') return cell.value || '';
  return cell || '';
}

function vitalsValueToLines(value) {
  const v = value && typeof value === 'object' ? value : {};
  return VITAL_FIELDS.map((f) => {
    const raw = String(vitalCellValue(v[f.key]) || '').trim();
    const display = raw === '' ? '___' : raw;
    const content = f.unit ? `${display} ${f.unit}` : display;
    return labeledTextLine(f.label, content);
  });
}

function rosValueToLines(value) {
  const v = value && typeof value === 'object' ? value : {};
  const lines = [];
  for (const f of ROS_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(v, f.key)) continue;
    const card = v[f.key];
    const description = card && typeof card === 'object' ? (card.description || '') : String(card || '');
    const label = card && typeof card === 'object' && card.label != null ? card.label : f.label;
    lines.push(labeledTextLine(label, description));
  }
  return lines;
}

function hasEmbed(ops) {
  return (ops || []).some(
    (op) =>
      op && op.insert && typeof op.insert === 'object' &&
      (Object.prototype.hasOwnProperty.call(op.insert, 'vitals-grid') ||
        Object.prototype.hasOwnProperty.call(op.insert, 'ros-grid')),
  );
}

// True when the delta contains at least one labeled body line we could collapse
// back into an embed, inside its matching section.
function hasConvertibleText(ops) {
  const lines = deltaToLines(ops);
  let section = null; // 'vitals' | 'ros' | 'other' | null
  for (const line of lines) {
    const text = lineText(line);
    if (line.kind === 'text' && (isHeadingLine(text) || lineIsStyledHeading(line))) {
      section = isVitalsHeading(text) ? 'vitals' : isRosHeading(text) ? 'ros' : 'other';
      continue;
    }
    if (line.kind !== 'text') { section = null; continue; }
    const parsed = splitLabeledLine(text);
    if (!parsed) continue;
    if (section === 'vitals' && matchFieldKey(parsed.label, VITAL_FIELDS)) return true;
    if (section === 'ros' && matchFieldKey(parsed.label, ROS_FIELDS)) return true;
  }
  return false;
}

/**
 * Which representation the document's Vitals/ROS sections are currently in.
 * @returns {'cards'|'text'|'none'}
 */
export function detectSectionFormat(delta) {
  const ops = delta && delta.ops;
  if (!Array.isArray(ops) || ops.length === 0) return 'none';
  if (hasEmbed(ops)) return 'cards';
  if (hasConvertibleText(ops)) return 'text';
  return 'none';
}

/**
 * Flatten the vitals-grid / ros-grid block embeds into plain labeled text
 * lines. Everything else (headers, the ROS preamble, Sleep-ROS prose) is left
 * exactly as-is.
 * @returns {{ ops: object[], stash: { vitals: object|null, ros: object|null } }}
 *   `stash` holds the original embed values so the reverse switch can restore
 *   data (e.g. ROS "positive" shading) that plain text can't carry.
 */
export function cardsToText(delta) {
  const lines = deltaToLines(delta && delta.ops);
  const out = [];
  const stash = { vitals: null, ros: null };
  for (const line of lines) {
    if (line.kind === 'embed' && line.embedKey === 'vitals-grid') {
      stash.vitals = line.embedValue;
      out.push(...vitalsValueToLines(line.embedValue));
    } else if (line.kind === 'embed' && line.embedKey === 'ros-grid') {
      stash.ros = line.embedValue;
      out.push(...rosValueToLines(line.embedValue));
    } else {
      out.push(line);
    }
  }
  return { ops: linesToOps(out), stash };
}

// "225 lb" → "225"; "120/80 mmHg" → "120/80"; "___" → "".
function parseVitalValue(rest) {
  const stripped = String(rest || '').replace(/\s*(?:lb|mmhg|bpm|%)\s*$/i, '').trim();
  if (stripped === '' || /^_+$/.test(stripped)) return '';
  return stripped;
}

function vitalsEmbedLine(items) {
  const value = {};
  for (const it of items) value[it.key] = { value: it.value };
  return { kind: 'embed', embedKey: 'vitals-grid', op: { insert: { 'vitals-grid': value } } };
}

function rosEmbedLine(items, stash) {
  const value = {};
  for (const it of items) {
    const field = ROS_FIELDS.find((f) => f.key === it.key);
    const stashCard = stash && stash.ros ? stash.ros[it.key] : undefined;
    const positive =
      stashCard && typeof stashCard.positive === 'boolean' ? stashCard.positive : field.positive;
    const card = { description: it.description, positive };
    // Carry a label only when the user renamed it away from the canonical one.
    if (normalizeLabel(it.label) !== normalizeLabel(field.label)) card.label = it.label;
    value[it.key] = card;
  }
  return { kind: 'embed', embedKey: 'ros-grid', op: { insert: { 'ros-grid': value } } };
}

/**
 * Collapse the plain-text Vitals / ROS lines (produced by cardsToText) back
 * into their block embeds. Only labeled lines inside the matching `▌ VITALS` /
 * `▌ REVIEW OF SYSTEMS` section are collapsed; preamble, Sleep-ROS prose and
 * any other content pass through unchanged.
 * @param {object} delta  the current document delta
 * @param {{ros?: object, vitals?: object}} [stash]  values captured by
 *   cardsToText, used to restore data not expressible in plain text.
 * @returns {{ ops: object[] }}
 */
export function textToCards(delta, stash) {
  const lines = deltaToLines(delta && delta.ops);
  const out = [];
  let section = null; // 'vitals' | 'ros' | 'other' | null
  let run = null;     // { type, items: [] }

  const flushRun = () => {
    if (!run) return;
    out.push(run.type === 'vitals' ? vitalsEmbedLine(run.items) : rosEmbedLine(run.items, stash));
    run = null;
  };

  for (const line of lines) {
    const text = lineText(line);
    if (line.kind === 'text' && (isHeadingLine(text) || lineIsStyledHeading(line))) {
      flushRun();
      section = isVitalsHeading(text) ? 'vitals' : isRosHeading(text) ? 'ros' : 'other';
      out.push(line);
      continue;
    }
    if (line.kind !== 'text') {
      flushRun();
      section = null;
      out.push(line);
      continue;
    }

    let consumed = false;
    if (section === 'vitals' || section === 'ros') {
      const fields = section === 'vitals' ? VITAL_FIELDS : ROS_FIELDS;
      const parsed = splitLabeledLine(text);
      const key = parsed && matchFieldKey(parsed.label, fields);
      if (key) {
        if (!run || run.type !== section) {
          flushRun();
          run = { type: section, items: [] };
        }
        if (section === 'vitals') {
          run.items.push({ key, value: parseVitalValue(parsed.rest) });
        } else {
          run.items.push({ key, label: parsed.label.trim(), description: parsed.rest.trim() });
        }
        consumed = true;
      }
    }
    if (!consumed) {
      flushRun();
      out.push(line);
    }
  }
  flushRun();
  return { ops: linesToOps(out) };
}

/**
 * Toggle the document between its structured (cards) and plain-text forms,
 * auto-detecting the current direction.
 * @param {object} delta  current document delta (`{ ops }`)
 * @param {{ stash?: {ros?: object, vitals?: object} }} [opts]  the stash
 *   returned by a prior cards→text switch, used to restore ROS shading.
 * @returns {{ delta: object, format: 'cards'|'text'|'none', changed: boolean,
 *   stash: object|null }}
 */
export function switchSectionFormat(delta, opts = {}) {
  const current = detectSectionFormat(delta);
  if (current === 'cards') {
    const { ops, stash } = cardsToText(delta);
    return { delta: { ops }, format: 'text', changed: true, stash };
  }
  if (current === 'text') {
    const { ops } = textToCards(delta, opts.stash);
    return { delta: { ops }, format: 'cards', changed: true, stash: opts.stash || null };
  }
  return { delta, format: 'none', changed: false, stash: opts.stash || null };
}
